import React, { useMemo, useRef, useState } from 'react';
import { Box, ModuleBase, Stack, ResponsiveImage, Link } from '@/components';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import Flicking from '@egjs/react-flicking';
import { fadeIn, carouselItemMotion } from '@/animations';
import DropdownArrow from '@/assets/caretDownLight.svg';
import { ExpertsListingModuleModel } from './ExpertsListingModule';
import { usePathname } from 'next/navigation';
import { LinkModel } from '@/types';

const ExpertsListingModuleCarousel = (data: ExpertsListingModuleModel) => {
  const { experts, cta } = data.props;

  const pathname = usePathname();
  const t = useTranslations();

  const [holding, setHolding] = useState(false);
  const [disabledNext, setDisabledNext] = useState(false);
  const [disabledPrev, setDisabledPrev] = useState(true);
  const carousel = useRef<Flicking>(null);

  const onCarouselChanged = () => {
    if (carousel.current) {
      if (carousel.current.index === 0) {
        setDisabledPrev(true);
      } else {
        setDisabledPrev(false);
      }
      if (carousel.current.index === (experts ? experts.length - 1 : 0)) {
        setDisabledNext(true);
      } else {
        setDisabledNext(false);
      }
    }
  };

  const next = () => {
    if (carousel.current && !carousel.current.animating) {
      carousel.current.next().catch(() => void 0);
    }
  };

  const prev = () => {
    if (carousel.current && !carousel.current.animating) {
      carousel.current.prev().catch(() => void 0);
    }
  };

  const ctaLink: LinkModel | null = useMemo(() => {
    if (cta && cta.length > 0) {
      return { href: cta?.match(/href="([^"]*)"/)?.[1] || '', text: cta?.match(/>([^<]+)<\/a>/)?.[1] || '' };
    } else {
      return null;
    }
  }, [cta]);

  if (!experts) {
    return null;
  }

  const isMembership = pathname === '/membership';

  return (
    <ModuleBase
      data={{ ...data, props: { ...data.props, backgroundColour: isMembership ? '#1B3E4E' : '' } }}
      noPadding
      className="overflow-hidden py-10 lg:py-18"
    >
      <div className="container flex flex-col justify-between">
        <Stack className="relative mb-10 lg:mb-14">
          {experts && (
            <Flicking
              className={classNames('!overflow-visible lg:-mx-1.5', holding ? 'cursor-grabbing' : 'cursor-grab')}
              ref={carousel}
              preventClickOnDrag
              preventDefaultOnDrag
              align="prev"
              bound
              resizeOnContentsReady
              onHoldStart={() => setHolding(true)}
              onHoldEnd={() => setHolding(false)}
              onChanged={onCarouselChanged}
            >
              {experts.map((expert, index) => (
                <Box
                  key={index}
                  animation={carouselItemMotion}
                  className="card flex w-2/3 flex-col px-2.5 md:w-1/3 lg:w-1/4"
                >
                  <div className="flex flex-col">
                    <div className="aspect-square">
                      {expert.image && (
                        <ResponsiveImage
                          animation={fadeIn}
                          image={expert.image}
                          widths={{ xs: 236, sm: 386, md: 386, lg: 230, xl: 325, xxl: 365 }}
                          heights={{ xs: 236, sm: 386, md: 386, lg: 230, xl: 325, xxl: 365 }}
                        />
                      )}
                    </div>

                    <div className="color-from-bg text-style-h4 mt-4">{expert.expertName}</div>
                    <div className="color-from-bg text-style-h5 mt-2 !text-[16px] leading-[18px] lg:!text-[22px] lg:!leading-[24px]">
                      {expert.jobTitle}
                    </div>
                  </div>
                  {expert.landingPageLink && (
                    <div className="mt-4">
                      <Link
                        className={classNames(
                          'btn btn-from-bg v2 underline',
                          isMembership ? 'text-sky' : 'text-[#9b9b9b]',
                        )}
                        link={expert.landingPageLink}
                      >
                        {t('general.$viewBio')}
                      </Link>
                    </div>
                  )}
                </Box>
              ))}
            </Flicking>
          )}
        </Stack>
        <Stack className="flex items-center justify-between">
          {ctaLink ? (
            <Link
              link={ctaLink}
              className={classNames(
                'btn primary v2 btn-from-bg',
                isMembership ? '!text-[#1B3E4E] hover:!text-white' : '',
              )}
            />
          ) : (
            <div></div>
          )}

          <Box animation={fadeIn} className="flex gap-3">
            <button
              className="cursor-pointer px-2 disabled:cursor-default disabled:opacity-50"
              onClick={prev}
              disabled={disabledPrev}
            >
              <DropdownArrow className="fill-from-bg rotate-90" />
            </button>
            <button
              className="cursor-pointer px-2 disabled:cursor-default disabled:opacity-50"
              onClick={next}
              disabled={disabledNext}
            >
              <DropdownArrow className="fill-from-bg -rotate-90" />
            </button>
          </Box>
        </Stack>
      </div>
    </ModuleBase>
  );
};

export default ExpertsListingModuleCarousel;
